import {CSSProperties} from 'react'
import {
  PaintTool,
  SLOT_FILL_STYLE,
  SLOT_TYPE_HINTS,
  SLOT_TYPE_LABELS,
} from '../helpers/availability'

interface ToolOption {
  value: PaintTool
  label: string
  hint: string
  swatch: CSSProperties
}

// Ordered hardest-constraint first, ending with the default state. Listing
// "Available" as the fourth option is deliberate: it makes the picker a
// complete legend of what a cell can be, which the grid otherwise never
// explains — a manager looking at a red block has nothing telling them what red
// means. It is an eraser in behaviour (absence of a slot IS availability), but
// naming it after the resulting state is what makes the model click.
const OPTIONS: ToolOption[] = [
  {
    value: 'unavailable',
    label: SLOT_TYPE_LABELS.unavailable,
    hint: SLOT_TYPE_HINTS.unavailable,
    swatch: SLOT_FILL_STYLE.unavailable,
  },
  {
    value: 'not_preferred',
    label: SLOT_TYPE_LABELS.not_preferred,
    hint: SLOT_TYPE_HINTS.not_preferred,
    swatch: SLOT_FILL_STYLE.not_preferred,
  },
  {
    value: 'preferred',
    label: SLOT_TYPE_LABELS.preferred,
    hint: SLOT_TYPE_HINTS.preferred,
    swatch: SLOT_FILL_STYLE.preferred,
  },
  {
    value: 'erase',
    label: 'Available',
    hint: 'Clears the block',
    swatch: {
      backgroundColor: 'var(--bs-body-bg)',
      border: '1px dashed var(--bs-gray-500)',
    },
  },
]

// Dated mode splits what "available" means, and the split is the whole point
// of the dated table:
//
//   erase      → this date says NOTHING, so the standing weekly rule applies
//   available  → this date says "I CAN work", overriding the weekly rule
//
// Weekly mode has no such distinction — there, absence IS availability — so
// the fifth option only appears here and 'erase' is relabelled to name the
// fallback rather than the outcome.
const DATED_OPTIONS: ToolOption[] = [
  ...OPTIONS.slice(0, -1),
  {
    value: 'available',
    label: SLOT_TYPE_LABELS.available,
    hint: SLOT_TYPE_HINTS.available,
    swatch: SLOT_FILL_STYLE.available,
  },
  {
    value: 'erase',
    label: 'Same as usual',
    hint: 'Falls back to the weekly pattern',
    swatch: {
      backgroundColor: 'var(--bs-body-bg)',
      border: '1px dashed var(--bs-gray-500)',
    },
  },
]

interface Props {
  value: PaintTool
  disabled?: boolean
  onChange: (tool: PaintTool) => void
  /** Dated mode: offer 'available' and rename the eraser. */
  dated?: boolean
}

/**
 * The paint palette. Each option renders a swatch in the *exact* fill the grid
 * will paint (both share SLOT_FILL_STYLE), so the control is self-documenting
 * rather than a row of coloured words the user has to map onto the grid.
 *
 * Semantically a radiogroup, not a set of toggle buttons: exactly one tool is
 * ever active, which gives arrow-key navigation and the right screen-reader
 * announcement for free.
 */
export const AvailabilityToolPicker = ({value, disabled, onChange, dated}: Props) => (
  <div>
    <div className='fs-8 fw-semibold text-gray-600 text-uppercase mb-2'>Paint with</div>
    <div className='d-flex flex-wrap gap-2' role='radiogroup' aria-label='Paint tool'>
      {(dated ? DATED_OPTIONS : OPTIONS).map((option) => {
        const selected = value === option.value
        return (
          <button
            key={option.value}
            type='button'
            role='radio'
            aria-checked={selected}
            aria-label={`${option.label} — ${option.hint}`}
            data-testid={`availability-tool-${option.value}`}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`btn btn-sm d-flex align-items-center text-start px-3 py-2 ${
              selected ? 'btn-light-primary border-primary' : 'btn-light border-transparent'
            }`}
            style={{borderWidth: 2, borderStyle: 'solid', minWidth: 150}}
          >
            <span
              aria-hidden='true'
              className='d-block rounded me-3 flex-shrink-0'
              style={{width: 18, height: 18, ...option.swatch}}
            />
            <span className='d-flex flex-column lh-1'>
              <span className='fw-bold fs-7 text-gray-900'>{option.label}</span>
              <span className='fs-8 text-gray-600 mt-1'>{option.hint}</span>
            </span>
            {selected && <i className='bi bi-check-lg fs-4 text-primary ms-3' aria-hidden='true' />}
          </button>
        )
      })}
    </div>
  </div>
)
